import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, Linkedin, Facebook, Instagram, CheckCircle } from 'lucide-react';
import { newsletterService } from '../services/newsletterService';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle'); // idle | loading | success | error
  const [newsletterMessage, setNewsletterMessage] = useState('');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNewsletterStatus('loading');
    try {
      const data = await newsletterService.subscribe(newsletterEmail.trim());
      setNewsletterStatus('success');
      setNewsletterMessage(data.message || t('footer.subscribeSuccess'));
      setNewsletterEmail('');
    } catch (err) {
      setNewsletterStatus('error');
      setNewsletterMessage(err.message || t('footer.subscribeError'));
    }
  };

  const products = [
    { name: t('footer.linkCmms'), path: '/cmms' },
    { name: t('footer.linkChatbot'), path: '/chatbot' },
    { name: t('footer.linkPricing'), path: '/pricing' },
  ];

  const software = [
    { name: t('footer.linkSfms'), path: '/software/sfms' },
    { name: t('footer.linkGms'), path: '/software/gms' },
  ];

  const services = [
    { name: t('footer.linkDigitalTransformation'), path: '/digital-transformation' },
    { name: t('footer.linkCustomDevelopment'), path: '/custom-development' },
  ];

  const resources = [
    { name: t('footer.linkDocumentation'), path: '/docs' },
    { name: t('footer.linkHelpCenter'), path: '/help' },
  ];

  const company = [
    { name: t('footer.linkContact'), path: '/contact' },
  ];

  const legal = [
    { name: t('footer.linkPrivacy'), path: '/privacy' },
    { name: t('footer.linkTerms'), path: '/terms' },
    { name: t('footer.linkSecurity'), path: '/security' },
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-7 gap-8 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-primary flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <span className="text-xl font-bold text-white">mwbTechs</span>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {t('footer.description')}
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <a href="mailto:info@mwbtechs.com" className="flex items-center gap-2 hover:text-primary-400 transition-colors">
                <Mail className="w-4 h-4" />
                <span>info@mwbtechs.com</span>
              </a>
              <a href="tel:+96176656040" className="flex items-center gap-2 hover:text-primary-400 transition-colors">
                <Phone className="w-4 h-4" />
                <span>+961 76 656 040</span>
              </a>
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.products')}</h4>
            <ul className="space-y-3">
              {products.map((item, index) => (
                <li key={index}>
                  <Link to={item.path} className="hover:text-primary-400 transition-colors">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Software */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.software')}</h4>
            <ul className="space-y-3">
              {software.map((item, index) => (
                <li key={index}>
                  <Link to={item.path} className="hover:text-primary-400 transition-colors">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.services')}</h4>
            <ul className="space-y-3">
              {services.map((item, index) => (
                <li key={index}>
                  <Link to={item.path} className="hover:text-primary-400 transition-colors">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.resources')}</h4>
            <ul className="space-y-3">
              {resources.map((item, index) => (
                <li key={index}>
                  <Link to={item.path} className="hover:text-primary-400 transition-colors">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.company')}</h4>
            <ul className="space-y-3">
              {company.map((item, index) => (
                <li key={index}>
                  <Link to={item.path} className="hover:text-primary-400 transition-colors">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter */}
        <div className="border-t border-gray-800 pt-8 mb-8">
          <div className="max-w-md">
            <h4 className="text-white font-semibold mb-2">{t('footer.newsletter')}</h4>
            <p className="text-gray-400 text-sm mb-4">
              {t('footer.newsletterDesc')}
            </p>
            {newsletterStatus === 'success' ? (
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <span>{newsletterMessage}</span>
              </div>
            ) : (
              <>
                <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                  <input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => {
                      setNewsletterEmail(e.target.value);
                      if (newsletterStatus === 'error') setNewsletterStatus('idle');
                    }}
                    placeholder={t('footer.emailPlaceholder')}
                    className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-primary-500 text-white disabled:opacity-50"
                    required
                    disabled={newsletterStatus === 'loading'}
                  />
                  <button
                    type="submit"
                    disabled={newsletterStatus === 'loading'}
                    className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {newsletterStatus === 'loading' ? '...' : t('footer.subscribe')}
                  </button>
                </form>
                {newsletterStatus === 'error' && (
                  <p className="text-red-400 text-xs mt-2">{newsletterMessage}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <div className="text-gray-400 text-sm">
              © {currentYear} {t('footer.copyright')}
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap gap-6 text-sm">
              {legal.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  className="text-gray-400 hover:text-primary-400 transition-colors"
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              {[
                { icon: Linkedin, url: 'https://linkedin.com' },
                { icon: Facebook, url: 'https://facebook.com' },
                { icon: Instagram, url: 'https://instagram.com' },
              ].map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-primary-600 transition-colors"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
